import { Box, Theme } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/styles";
import { AxiosResponse } from "axios";
import { DropzoneArea } from "material-ui-dropzone";
import React, { useContext, useState } from "react";
import CustomButton from "../../components/Button/CustomButton";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import { HttpErrorType } from "../../models/HttpErrorType";
import { tesaBlue, tesaBlue4, tesaWhite } from "../../styles/colors";

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    "& > * + *": {
      marginLeft: theme.spacing(3),
    },
  },
  MuiDropzoneArea: {
    "& > * + *": {
      marginTop: theme.spacing(5),
    },
    "& .MuiDropzoneArea-root": {
      minHeight: 200,
      borderWidth: "2px",
      strokeDasharray: "4 6",
      borderRadius: 0,
      outlineColor: tesaBlue,

      "&.MuiDropzoneArea-active": {
        animation: "none",
        backgroundColor: tesaBlue4,
        backgroundImage: "none",
        borderColor: tesaBlue,
      },

      "& .MuiDropzoneArea-text": {
        fontSize: "16px",
        position: "absolute",
        top: "40%",
        left: "50%",
        transform: "translate(-50%, 0)",
      },

      "& .MuiDropzoneArea-icon": {
        position: "absolute",
        width: "40px",
        height: "40px",
        top: "40%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      },

      "& .MuiDropzonePreviewList-root": {
        paddingLeft: 12,
        position: "absolute",
        bottom: "6%",
      },
    },
  },
  progressWrapper: {
    display: "flex",
    lineHeight: "40px",
  },
  progress: {
    display: "inline-flex",
    position: "relative",
    marginRight: theme.spacing(2),
    "& .MuiCircularProgress-colorPrimary": {
      color: tesaBlue,
    },
    "& > .MuiBox-root": {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      position: "absolute",
      alignItems: "center",
      justifyContent: "center",
    },
  },
}));

function MBToKB(amountMB: number): number {
  return 1024 ** 2 * amountMB;
}

type ActionConfig = {
  text: string;
  handleAction: () => void;
};

interface FileUploadProps {
  acceptedFiles: Record<string, string>;
  additionalAction?: ActionConfig;
  buttonLabel: string;
  disablePrimary?: boolean;
  templateHref?: string;
  downloadTemplate?: () => Promise<void>;
  maxFileSizeMB: number;
  successMessage: string;
  handleError?: (error?: HttpErrorType) => void;
  upload: (
    payload: FormData,
    progressCallback?: (progressEvent: { loaded: number; total: number }) => void,
  ) => Promise<AxiosResponse>;
}

const FileUpload: React.FC<FileUploadProps> = (props) => {
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const { handleHttpError, showErrorSnackbar, showSnackbar } = useContext(ErrorSnackbarContext);

  const classes = useStyles();
  const {
    acceptedFiles,
    buttonLabel,
    disablePrimary,
    templateHref,
    downloadTemplate,
    maxFileSizeMB,
    successMessage,
    handleError,
    upload,
  } = props;
  const maxFileSize = MBToKB(maxFileSizeMB);

  const reset = () => {
    setProgress(0);
    setUploading(false);
    setSelectedFiles([]);
  };

  const handleUpload = (file: File) => {
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    upload(formData, (progressEvent) => {
      setProgress((progressEvent.loaded / progressEvent.total) * 100);
    })
      .then(() => {
        showSnackbar(successMessage, "success");
      })
      .catch((err) => {
        if (handleError) {
          handleError(err);
        } else {
          handleHttpError(err);
        }
      })
      .finally(() => {
        reset();
      });
  };

  return (
    <div className={classes.MuiDropzoneArea}>
      <DropzoneArea
        onChange={setSelectedFiles}
        acceptedFiles={Object.values(acceptedFiles)}
        showAlerts={false}
        onAlert={(message) => {
          if (message) {
            showErrorSnackbar(message);
          }
        }}
        getFileAddedMessage={() => ""}
        getFileRemovedMessage={() => ""}
        getDropRejectMessage={(rejectedFile: File) => {
          if (rejectedFile.size <= maxFileSize) {
            return `Upload not possible. Please use one of the following file types: ${Object.keys(acceptedFiles).join(
              ", ",
            )}`;
          }
          return "Upload not possible. File size limit (5 MB) exceeded.";
        }}
        getFileLimitExceedMessage={() => "Upload not possible. You can only upload one file."}
        maxFileSize={maxFileSize}
        showPreviewsInDropzone
        useChipsForPreview
        previewChipProps={{ style: { backgroundColor: tesaWhite } }}
        filesLimit={1}
      />
      <div className={classes.buttonContainer}>
        {uploading ? (
          <div className={classes.progressWrapper}>
            <Box className={classes.progress}>
              <CircularProgress variant="determinate" value={progress} />
              <Box>
                <Typography variant="caption" component="div" color="textSecondary">{`${Math.round(
                  progress,
                )}%`}</Typography>
              </Box>
            </Box>
          </div>
        ) : (
          <CustomButton
            primary
            text={buttonLabel}
            type="submit"
            onClick={() => {
              handleUpload(selectedFiles[0]);
            }}
            disabled={selectedFiles.length === 0 || disablePrimary}
          />
        )}
        {templateHref && (
          <Link component={CustomButton} download href={templateHref} text="Download Template" underline="none" />
        )}
        {downloadTemplate && (
          <CustomButton download onClick={() => downloadTemplate().catch(handleHttpError)} text="Download Template" />
        )}
      </div>
    </div>
  );
};
export default FileUpload;
